import { useState, memo, useEffect, useMemo } from 'react';

import { QuestionCircleOutlined } from '@ant-design/icons';
import { Popover, Input, Radio, message, InputNumber } from 'antd';
import { styled } from 'styled-components';

import Colon from '@/components/Colon';
import CustomCheckBox from '@/components/CustomCheckBox';
import SvgIcon from '@/components/SvgIcon';
import { _globalMixSortFilter, _globalPriceAndStock } from '@/utils/_globalBuriedPoint';
import { buriedPoint, getBuriedPointCP } from '@/utils/buriedPoint';

const StyledRadio = styled(Radio)`
  &.ant-radio-wrapper {
    margin-right: 0;
  }
`;

const StyledRadioGroupDiv = styled.div`
  .ant-radio-wrapper {
    margin-bottom: 6px;
    width: 107px;
    text-align: center;
    margin-right: 0;
    display: flex;
    justify-content: center;
  }
`;

const PopoverStockEnums = [
  {
    name: '总库存',
    code: 'all',
  },
  {
    name: '广东仓',
    code: 'gd',
  },
  {
    name: '江苏仓',
    code: 'js',
  },
];

const MixSortFilter = (props) => {
  const {
    totalCount,
    changeSendSearchParams,
    sendSearchParams,
    hasBorder,
    mixSortDisabled,
    isMro,
    isCatalogPage,
    hiddenPlace = false,
  } = props;
  const brandPlaceFilterKey = isCatalogPage ? 'brandOriginFilter' : 'brandPlaceFilter';
  const [popoverParams, setPopoverParams] = useState({
    startPrice: '',
    endPrice: '',
    demandNumber: '',
    satisfyStockType: '',
  });
  const [popoverActive, setPopoverActive] = useState(false);
  const [priceAndStockVisible, setPriceAndStockVisible] = useState(false);
  const [manualControl, setManualControl] = useState(false);
  const [stockVisible, setStockVisible] = useState(false);
  const [stockManualControl, setStockManualControl] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [storeStock, setStoreStock] = useState('总库存');
  const [stockSort, setStockSort] = useState(''); // sx 升序 js 降序
  const [stockLoading, setStockLoading] = useState(false);

  const [isNeedResetList, setIsNeedResetList] = useState(false);

  useEffect(() => {
    if (
      sendSearchParams.startPrice ||
      sendSearchParams.endPrice ||
      sendSearchParams.demandNumber ||
      sendSearchParams.satisfyStockType
    ) {
      setPopoverActive(true);
    } else {
      setPopoverActive(false);
    }
  }, [
    sendSearchParams.startPrice,
    sendSearchParams.endPrice,
    sendSearchParams.demandNumber,
    sendSearchParams.satisfyStockType,
  ]);

  const sortTypeChange = (type) => {
    if (mixSortDisabled) return;
    const mixFn = () => {
      setStockSort('');
      changeSendSearchParams({
        sortNumber: 0,
        currentPage: 1,
      });
      buriedPoint({
        op: _globalMixSortFilter['comprehensive_sort'],
      });
    };
    const priceFn = () => {
      setStockSort('');
      changeSendSearchParams({
        sortNumber: sendSearchParams.sortNumber === 2 ? 1 : sendSearchParams.sortNumber === 1 ? 2 : 1,
        currentPage: 1,
      });
      buriedPoint({
        op: _globalMixSortFilter['price'],
      });
    };
    const stockFn = () => {
      if (stockLoading) return;
      setStockLoading(true);
      let stockSortState;
      if (!stockSort || stockSort === 'sx') {
        stockSortState = 'jx';
      } else {
        stockSortState = 'sx';
      }
      setStockSort(stockSortState);
      let sortNumberState = '';
      if (storeStock === '总库存' && stockSortState === 'jx') {
        sortNumberState = 8;
      } else if (storeStock === '总库存' && stockSortState === 'sx') {
        sortNumberState = 9;
      } else if (storeStock === '广东仓' && stockSortState === 'jx') {
        sortNumberState = 10;
      } else if (storeStock === '广东仓' && stockSortState === 'sx') {
        sortNumberState = 11;
      } else if (storeStock === '江苏仓' && stockSortState === 'jx') {
        sortNumberState = 12;
      } else if (storeStock === '江苏仓' && stockSortState === 'sx') {
        sortNumberState = 13;
      }
      changeSendSearchParams(
        {
          sortNumber: sortNumberState,
          currentPage: 1,
        },
        true,
        () => {
          setStockLoading(false);
        }
      );

      buriedPoint({
        op: _globalMixSortFilter['stock_sort'],
      });
    };
    const sellFn = () => {
      setStockSort('');
      changeSendSearchParams({
        sortNumber: 6,
        currentPage: 1,
      });
      buriedPoint({
        op: _globalMixSortFilter['sales'],
      });
    };
    const fnMap = {
      mix: mixFn,
      price: priceFn,
      stock: stockFn,
      sell: sellFn,
    };
    typeof fnMap[type] === 'function' && fnMap[type]();
  };

  const changeSecondKeyword = (e) => {
    setInputValue(e.target.value);
    if (!e.target.value && isNeedResetList) {
      changeSendSearchParams({
        secondKeyword: '',
        currentPage: 1,
      });
      setIsNeedResetList(false);
    }
  };

  const changePopoverParams = (e, type) => {
    const startPriceFn = () => {
      setPopoverParams({
        ...popoverParams,
        startPrice: e,
      });
    };
    const endPriceFn = () => {
      setPopoverParams({
        ...popoverParams,
        endPrice: e,
      });
    };
    const needNumFn = () => {
      if (e && !/^[1-9]\d*$/.test(e)) {
        setPopoverParams({
          ...popoverParams,
          demandNumber: '',
        });
        return message.error('数量格式不正确，请输入正确的数值');
      }
      setPopoverParams({
        ...popoverParams,
        demandNumber: e,
      });
    };
    const stockSatisfyRadioFn = () => {
      setPopoverParams({
        ...popoverParams,
        satisfyStockType: popoverParams.satisfyStockType === e.target.value ? '' : e.target.value,
      });
    };
    const fnMap = {
      startPrice: startPriceFn,
      endPrice: endPriceFn,
      demandNumber: needNumFn,
      satisfyStockType: stockSatisfyRadioFn,
    };
    typeof fnMap[type] === 'function' && fnMap[type]();
  };

  const checkPriceValid = () => {
    const startPrice = popoverParams.startPrice;
    if (startPrice && !/^\d+(\.\d+)?$/.test(startPrice)) {
      message.error('格式不正确，请输入正确的价格');
      return false;
    }
    if (startPrice && String(startPrice).indexOf('.') > -1 && String(startPrice).split('.')[1].length > 4) {
      message.error('小数最多支持4位');
      return false;
    }
    const endPrice = popoverParams.endPrice;
    if (endPrice && !/^\d+(\.\d+)?$/.test(endPrice)) {
      message.error('格式不正确，请输入正确的价格');
      return false;
    }
    if (endPrice && String(endPrice).indexOf('.') > -1 && String(endPrice).split('.')[1].length > 4) {
      message.error('小数最多支持4位');
      return false;
    }
    if (startPrice && endPrice && +startPrice > +endPrice) {
      message.error('最小值不能大于最大值，请重新输入');
      return false;
    }
    return true;
  };

  const resetPriceAndStock = () => {
    setPriceAndStockVisible(false);
    setManualControl(false);
    setPopoverParams({
      startPrice: '',
      endPrice: '',
      demandNumber: '',
      satisfyStockType: '',
    });
    if (
      sendSearchParams.startPrice ||
      sendSearchParams.endPrice ||
      sendSearchParams.demandNumber ||
      sendSearchParams.satisfyStockType
    ) {
      changeSendSearchParams({
        startPrice: '',
        endPrice: '',
        demandNumber: '',
        satisfyStockType: '',
        currentPage: 1,
      });
      message.success('已恢复默认价格及库存');
    }
  };

  const applyPopoverParams = () => {
    if (!checkPriceValid()) {
      return false;
    }
    setPriceAndStockVisible(false);
    setManualControl(false);
    changeSendSearchParams({
      ...popoverParams,
      currentPage: 1,
    });
    buriedPoint({
      op: _globalPriceAndStock['apply_filter'],
    });
  };

  const priceAndStockContent = () => {
    return (
      <div className={'mt-[10px] w-[370px] py-[18px] pl-[16px] pr-[21px] text-[14px]  text-[#1F1F1F]'}>
        <div className={'mb-[10px] flex items-center justify-center'}>
          <div className={'w-[72px]'}>价格范围：</div>
          <div className={'flex-1'}>
            <InputNumber
              placeholder={'最小值'}
              className={'!w-[120px]'}
              value={popoverParams.startPrice}
              onChange={(e) => {
                changePopoverParams(e, 'startPrice');
              }}
              controls={false}
              max={99999999}
              maxLength={8}
            />
            <span className={'inline-block w-[20px] text-center'}>-</span>
            <InputNumber
              placeholder={'最大值'}
              className={'!w-[120px]'}
              value={popoverParams.endPrice}
              onChange={(e) => {
                changePopoverParams(e, 'endPrice');
              }}
              controls={false}
              max={99999999}
              maxLength={8}
            />
          </div>
        </div>
        <div className={'mb-[10px] flex items-center justify-center'}>
          <div className={'w-[72px]'}> 需求数量：</div>
          <div className={'flex-1'}>
            <InputNumber
              placeholder={'请输入需求数量'}
              className={'!w-[260px]'}
              value={popoverParams.demandNumber}
              onChange={(e) => {
                changePopoverParams(e, 'demandNumber');
              }}
              controls={false}
              max={99999999}
              maxLength={8}
            />
          </div>
        </div>
        <div className={'mb-[19px] flex items-center justify-center'}>
          <div className={'w-[72px]'}>库存满足：</div>
          <div className={'flex flex-1 justify-between'}>
            {PopoverStockEnums.map((item, index) => {
              return (
                <StyledRadio
                  key={index}
                  value={item.code}
                  checked={item.code === popoverParams.satisfyStockType}
                  onClick={(e) => changePopoverParams(e, 'satisfyStockType')}
                >
                  {item.name}
                </StyledRadio>
              );
            })}
          </div>
        </div>
        <div className={'flex justify-end'}>
          <button
            onClick={resetPriceAndStock}
            className={
              'flex h-[32px] w-[80px] items-center justify-center rounded-[6px]  border border-solid border-[rgba(0,0,0,0.15)] bg-[#FFFFFF] text-[#000000] hover:border-[#0093E6] hover:text-[#0093E6]'
            }
          >
            恢复默认
          </button>
          <button
            className={
              'ml-[10px] flex h-[32px] w-[80px] items-center justify-center rounded-[6px]  bg-[#0093E6] text-[#FFFFFF] hover:opacity-80'
            }
            onClick={applyPopoverParams}
          >
            应用筛选
          </button>
        </div>
      </div>
    );
  };

  const changeStockRadio = (e) => {
    if (stockLoading) return;
    setStockLoading(true);
    let stockSortState = stockSort;
    if (!stockSortState) {
      stockSortState = 'jx';
      setStockSort(stockSortState);
    }
    let opValue = '';
    let sortNumberState = '';
    if (e.target.value === '总库存' && stockSortState === 'jx') {
      opValue = _globalMixSortFilter['all_stock'];
      sortNumberState = 8;
    } else if (e.target.value === '总库存' && stockSortState === 'sx') {
      opValue = _globalMixSortFilter['all_stock'];
      sortNumberState = 9;
    } else if (e.target.value === '广东仓' && stockSortState === 'jx') {
      opValue = _globalMixSortFilter['gd_stock'];
      sortNumberState = 10;
    } else if (e.target.value === '广东仓' && stockSortState === 'sx') {
      opValue = _globalMixSortFilter['gd_stock'];
      sortNumberState = 11;
    } else if (e.target.value === '江苏仓' && stockSortState === 'jx') {
      opValue = _globalMixSortFilter['js_stock'];
      sortNumberState = 12;
    } else if (e.target.value === '江苏仓' && stockSortState === 'sx') {
      opValue = _globalMixSortFilter['js_stock'];
      sortNumberState = 13;
    }
    setStoreStock(e.target.value);
    setStockVisible(false);
    setStockManualControl(false);
    changeSendSearchParams(
      {
        sortNumber: sortNumberState,
        currentPage: 1,
      },
      true,
      () => {
        setStockLoading(false);
      }
    );
    buriedPoint({
      op: opValue,
    });
  };

  const handleVisibleChange = (newVisible) => {
    if (!manualControl) {
      setPriceAndStockVisible(newVisible);
    }
  };

  const handleStockVisibleChange = (newVisible) => {
    if (!stockManualControl) {
      setStockVisible(newVisible);
    }
  };

  const stockRadioList = () => {
    return (
      <StyledRadioGroupDiv className={'mt-[10px] flex h-[106px] w-[107px] flex-col items-center justify-center'}>
        <Radio.Group
          value={storeStock}
          onChange={(e) => changeStockRadio(e)}
          options={[
            { value: '总库存', label: '总库存' },
            { value: '广东仓', label: '广东仓' },
            { value: '江苏仓', label: '江苏仓' },
          ]}
        />
      </StyledRadioGroupDiv>
    );
  };

  const handleClickSearch = () => {
    if (mixSortDisabled) return;
    if (inputValue) {
      setIsNeedResetList(true);
    }
    changeSendSearchParams({
      secondKeyword: inputValue,
      currentPage: 1,
    });
    let cp = getBuriedPointCP();
    if (cp === 'catalog') {
      cp = 'catalog_search';
    }
    buriedPoint({
      op: _globalMixSortFilter['search_in_result'],
      cp,
    });
  };

  const [inputHoverStatus, setInputHoverStatus] = useState(false);
  const InputIcon = useMemo(() => {
    if (inputHoverStatus) {
      return 'search-icon-hover';
    }
    return 'search-icon';
  }, [inputHoverStatus]);

  return (
    <div
      className={`${hasBorder ? 'border-t border-solid border-[#F1F2F4]' : ''} mx-[21px] flex h-[51px] items-stretch justify-between text-[14px]`}
    >
      <div className={'flex items-center text-[14px] text-[#444444]'}>
        <div
          className={`mr-[31px] ${mixSortDisabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer hover:text-[#0093E6]'} ${sendSearchParams.sortNumber === 0 ? 'font-bold text-[#0093e6]' : ''}`}
          onClick={() => {
            sortTypeChange('mix');
          }}
        >
          综合排序
        </div>
        <div
          className={`mr-[24px] ${mixSortDisabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer hover:text-[#0093E6]'} ${sendSearchParams.sortNumber === 1 || sendSearchParams.sortNumber === 2 ? 'font-bold text-[#0093e6]' : ''}`}
          onClick={() => {
            sortTypeChange('price');
          }}
        >
          <div className={'flex items-center'}>
            <div className={`mr-[2px]`}>价格</div>
            <div
              className={`${sendSearchParams.sortNumber === 1 || sendSearchParams.sortNumber === 2 ? 'opacity-100' : 'opacity-0'}`}
            >
              <div
                className={`mb-[3px] w-[5px] border-[5px] border-solid border-[transparent] ${sendSearchParams.sortNumber === 1 ? 'border-b-[#0093E6]' : 'border-b-[#C0C0C0]'}`}
              ></div>
              <div
                className={`w-[5px] border-[5px] border-solid border-[transparent] ${sendSearchParams.sortNumber === 2 ? 'border-t-[#0093E6]' : 'border-t-[#C0C0C0]'}`}
              ></div>
            </div>
          </div>
        </div>
        {mixSortDisabled ? (
          <div
            className={`mr-[24px] cursor-not-allowed opacity-40  ${[8, 9, 10, 11, 12, 13].includes(sendSearchParams.sortNumber) ? 'font-bold text-[#0093e6]' : ''}`}
          >
            <div className={'flex items-center'}>
              <div className="mr-[2px] text-[14px]">{storeStock}</div>
              <div
                className={`${[8, 9, 10, 11, 12, 13].includes(sendSearchParams.sortNumber) ? 'opacity-100' : 'opacity-0'}`}
              >
                <div
                  className={`mb-[3px] w-[5px] border-[5px] border-solid border-[transparent] ${stockSort === 'sx' ? 'border-b-[#0093E6]' : 'border-b-[#C0C0C0]'}`}
                ></div>
                <div
                  className={`w-[5px] border-[5px] border-solid border-[transparent] ${stockSort === 'jx' ? 'border-t-[#0093E6]' : 'border-t-[#C0C0C0]'}`}
                ></div>
              </div>
            </div>
          </div>
        ) : (
          <div
            className={`mr-[24px] cursor-pointer hover:text-[#0093E6] ${[8, 9, 10, 11, 12, 13].includes(sendSearchParams.sortNumber) ? 'font-bold text-[#0093e6]' : ''}`}
          >
            <Popover
              placement={'bottom'}
              content={stockRadioList}
              overlayClassName="lcsc-popover-wrapper lcsc-popover-wrapper-hide-top-arrow"
              motion={null}
              transitionName=""
              animation=""
              trigger={'hover'}
              open={stockVisible}
              onOpenChange={handleStockVisibleChange}
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
              align={{
                offset: [-6, 0],
              }}
            >
              <div
                className={'flex items-center'}
                onClick={() => {
                  sortTypeChange('stock');
                }}
              >
                <div className="mr-[2px]">{storeStock}</div>
                <div
                  className={`${[8, 9, 10, 11, 12, 13].includes(sendSearchParams.sortNumber) ? 'opacity-100' : 'opacity-0'}`}
                >
                  <div
                    className={`mb-[3px] w-[5px] border-[5px] border-solid border-[transparent] ${stockSort === 'sx' ? 'border-b-[#0093E6]' : 'border-b-[#C0C0C0]'}`}
                  ></div>
                  <div
                    className={`w-[5px] border-[5px] border-solid border-[transparent] ${stockSort === 'jx' ? 'border-t-[#0093E6]' : 'border-t-[#C0C0C0]'}`}
                  ></div>
                </div>
              </div>
            </Popover>
          </div>
        )}
        <div
          className={`_lg:mr-[31px] mr-[21px] pr-[31px]  ${mixSortDisabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer  hover:text-[#0093E6]'} border-r-[1px] border-solid border-[#DCDFE6] ${sendSearchParams.sortNumber === 6 ? 'font-bold text-[#0093e6]' : ''}`}
          onClick={() => {
            sortTypeChange('sell');
          }}
        >
          销量
        </div>

        {mixSortDisabled ? (
          <div className={'_lg:mr-[27px] mr-[21px]  cursor-not-allowed opacity-40'}>
            <span className={`pr-[3px] text-[14px] ${popoverActive ? 'text-[#0093E6]' : ''}`}>价格/库存筛选</span>
            <QuestionCircleOutlined style={{ color: '#8C8C8C', transform: 'translateY(1px)' }} />
          </div>
        ) : (
          <div className={'_lg:mr-[27px] mr-[21px]'}>
            <Popover
              placement={'bottomLeft'}
              content={priceAndStockContent}
              overlayClassName="lcsc-popover-wrapper lcsc-popover-wrapper-hide-top-arrow"
              motion={null}
              transitionName=""
              animation=""
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
              trigger={'hover'}
              open={priceAndStockVisible}
              onOpenChange={handleVisibleChange}
            >
              <span
                className={`cursor-pointer pr-[3px] text-[14px] ${popoverActive ? 'text-[#0093E6]' : ''} hover:text-[#0093E6]`}
              >
                价格/库存筛选
              </span>
            </Popover>
            <Popover
              placement={'bottom'}
              overlayClassName="lcsc-popover-wrapper lcsc-popover-wrapper-hide-top-arrow"
              motion={null}
              transitionName=""
              animation=""
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
              content={
                <div className={'mt-[10px] w-[270px] px-[10px] py-[13px] text-[12px] text-[#6C6C6C]'}>
                  <p>价格筛选：默认对第一阶梯价格进行筛选，若填写了需求数量，会与需求数量所在阶梯价格进行筛选。</p>
                  <p>库存筛选：需要与需求数量结合，才能对相关仓库进行库存筛选。</p>
                </div>
              }
            >
              <QuestionCircleOutlined style={{ color: '#8C8C8C', transform: 'translateY(1px)' }} />
            </Popover>
          </div>
        )}

        <div className={`_lg:mr-[31px] mr-[26px] ${!mixSortDisabled ? '' : 'cursor-not-allowed opacity-40'}`}>
          <CustomCheckBox
            className={`so-custom-checkbox ${!mixSortDisabled ? '' : 'so-custom-checkbox-not'} ${props?.checkboxCls}`}
            disabled={mixSortDisabled}
            checked={sendSearchParams.spotFilter === 0}
            onChange={(e) => {
              changeSendSearchParams({
                spotFilter: e.target.checked ? 0 : 1,
                currentPage: 1,
              });
              let cp = getBuriedPointCP();
              if (cp === 'catalog') {
                cp = 'catalog_search';
              }
              buriedPoint({
                op: _globalMixSortFilter['spot_commodity'],
                cp,
              });
            }}
          >
            现货
          </CustomCheckBox>
        </div>
        <div className={`_lg:mr-[31px] mr-[26px] ${!mixSortDisabled ? '' : 'cursor-not-allowed opacity-40'}`}>
          <CustomCheckBox
            className={`so-custom-checkbox ${!mixSortDisabled ? '' : 'so-custom-checkbox-not'}  ${props?.checkboxCls}`}
            checked={sendSearchParams.discountFilter === 0}
            disabled={mixSortDisabled}
            onChange={(e) => {
              changeSendSearchParams({
                discountFilter: e.target.checked ? 0 : 1,
                currentPage: 1,
              });
              buriedPoint({
                ty: 'click',
                op: 'discount_commodity',
              });
            }}
          >
            折扣
          </CustomCheckBox>
        </div>
        {!isMro && (
          <>
            <div className={`_lg:mr-[31px] mr-[26px] ${!mixSortDisabled ? '' : 'cursor-not-allowed opacity-40'}`}>
              <CustomCheckBox
                className={`so-custom-checkbox ${!mixSortDisabled ? '' : 'so-custom-checkbox-not'}  ${props?.checkboxCls}`}
                checked={sendSearchParams.hasDataFile}
                disabled={mixSortDisabled}
                onChange={(e) => {
                  changeSendSearchParams({
                    hasDataFile: e.target.checked,
                    currentPage: 1,
                  });
                  buriedPoint({
                    op: _globalMixSortFilter['filter_pdf'],
                  });
                }}
              >
                数据手册
              </CustomCheckBox>
            </div>
            {!hiddenPlace && (
              <>
                <div className={'_lg:mr-[31px] mr-[26px] relative'}>
                  <CustomCheckBox
                    className={`so-custom-checkbox ${props?.checkboxCls}`}
                    checked={sendSearchParams[brandPlaceFilterKey] === '中国大陆$中国台湾'}
                    onChange={(e) => {
                      changeSendSearchParams({
                        [brandPlaceFilterKey]: e.target.checked ? '中国大陆$中国台湾' : '',
                        currentPage: 1,
                      });
                      buriedPoint({
                        pf: 'pc',
                        ty: 'click',
                        op: _globalMixSortFilter['CN_brand'],
                        data: {
                          kw: props?.globalSearchKeyword,
                        },
                      });
                    }}
                  >
                    国产品牌
                  </CustomCheckBox>
                  <SvgIcon
                    app="so"
                    iconName={'condition-hot'}
                    className={'w-[32px] h-[14px] absolute right-[-26px] top-[-12px]'}
                  ></SvgIcon>
                </div>
                <CustomCheckBox
                  className={`so-custom-checkbox ${props?.checkboxCls}`}
                  checked={sendSearchParams[brandPlaceFilterKey] === '国际'}
                  onChange={(e) => {
                    changeSendSearchParams({
                      [brandPlaceFilterKey]: e.target.checked ? '国际' : '',
                      currentPage: 1,
                    });
                    buriedPoint({
                      pf: 'pc',
                      ty: 'click',
                      op: _globalMixSortFilter['global_brand'],
                      data: {
                        kw: props?.globalSearchKeyword,
                      },
                    });
                  }}
                >
                  国际品牌
                </CustomCheckBox>
              </>
            )}
          </>
        )}
      </div>
      {mixSortDisabled ? (
        <></>
      ) : (
        <div className={`flex items-center text-[12px] text-[#69788a]`}>
          <span className={'pr-[8px]'}>
            自营结果数
            <Colon />
            <span className={'text-[#444444]'}>{totalCount >= 1500 ? '1500+' : totalCount}</span>
          </span>
          <div
            className={'w-[180px]'}
            onMouseEnter={() => setInputHoverStatus(true)}
            onMouseLeave={() => setInputHoverStatus(false)}
          >
            <Input
              className={`!h-[28px] !w-[180px] !text-[12px]`}
              placeholder={'在结果中再搜索'}
              allowClear
              value={inputValue}
              onChange={changeSecondKeyword}
              maxLength={52}
              suffix={
                <SvgIcon
                  app="so"
                  iconName={InputIcon}
                  onClick={handleClickSearch}
                  className={'size-[15px] cursor-pointer'}
                ></SvgIcon>
              }
              onPressEnter={handleClickSearch}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(MixSortFilter);
